import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { usePageTitle, useMetaDescription, useSocialMeta } from '@/hooks/usePageTitle';
import { useLocale } from '@/hooks/useLocale';
import { PublicShell } from '@/components/visual/PublicShell';
import { PageHeader } from '@/components/visual/PagePrimitives';
import { LocaleLink } from '@/components/LocaleLink';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Users, Star, Search, Globe, MapPin, Award, Briefcase, ExternalLink, Linkedin, Loader2, Send, Sparkles, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import logger from '@/lib/logger';

const API = '/api';
const EMAIL_RE = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const ALL = '__all__';
const toArr = (s) => (s || '').split(',').map((x) => x.trim()).filter(Boolean);
const APPLY_EMPTY = {
  name: '', email: '', jobTitle: '', company: '', bio: '',
  specialties: '', regions: '', languages: '', rate_range: '', years_experience: '',
  website: '', linkedin: '', consent: false,
};

function ConsultantCard({ c, onRequest, t }) {
  const frame = c.is_featured
    ? 'border border-[#66707A]'
    : 'border border-[#DDE1E5] hover:border-[#66707A]';
  return (
    <div
      className={`consultant-card relative flex flex-col rounded-lg bg-white p-6 transition-colors ${frame}`}
      data-testid={`consultant-card-${c.consultant_id}`}
    >
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-md flex items-center justify-center bg-[#F5F6F7] text-[#171A1D] font-semibold text-lg shrink-0">
          {c.avatar_initials}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-display font-semibold text-[#171A1D] text-base truncate">{c.name}</h3>
            {c.is_featured && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-[#527F19] text-white font-semibold" data-testid="featured-badge">
                <Star className="w-3 h-3 fill-current" /> {t('marketplace.card.featured')}
              </span>
            )}
            {c.is_sample && (
              <span className="px-2 py-0.5 rounded-full text-xs bg-[#B7791F]/[0.12] text-[#8A5A0B] font-medium">{t('marketplace.card.sample')}</span>
            )}
          </div>
          <p className="text-sm text-[#527F19] font-medium truncate">{c.title}</p>
          <p className="text-xs text-[#8A929B] truncate">{c.company}</p>
        </div>
      </div>

      <p className="mt-4 text-sm text-[#3A4249] leading-relaxed line-clamp-3">{c.bio}</p>

      <div className="mt-4 flex flex-wrap gap-1.5">
        {(c.specialties || []).map((s) => (
          <span key={s} className="px-2 py-0.5 rounded-md text-xs bg-[#F1F8E8] text-[#527F19]">{s}</span>
        ))}
      </div>

      <div className="mt-4 space-y-1.5 text-xs text-[#66707A]">
        <div className="flex items-center gap-1.5"><Globe className="w-3.5 h-3.5 text-[#8A929B]" /> {(c.languages || []).join(', ')}</div>
        <div className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-[#8A929B]" /> {(c.regions || []).join(', ')}</div>
        {(c.certifications || []).length > 0 && (
          <div className="flex items-center gap-1.5"><Award className="w-3.5 h-3.5 text-[#8A929B]" /> {(c.certifications || []).join(', ')}</div>
        )}
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3 text-xs text-[#66707A] border-t border-[#DDE1E5] pt-3">
        {c.years_experience > 0 && <span className="flex items-center gap-1"><Briefcase className="w-3.5 h-3.5" /> {t('marketplace.card.experience', { count: c.years_experience })}</span>}
        {c.completed_projects > 0 && <span>{t('marketplace.card.projects', { count: c.completed_projects })}</span>}
        {c.rate_range && <span className="ml-auto font-data text-[#3A4249]">{c.rate_range}</span>}
      </div>

      <div className="mt-5 flex items-center gap-2">
        <Button
          className="flex-1 bg-[#527F19] hover:bg-[#3F6114] text-white font-semibold shadow-card transition-colors"
          onClick={() => onRequest(c)}
          data-testid={`request-intro-btn-${c.consultant_id}`}
        >
          <Send className="w-4 h-4 mr-2" /> {t('marketplace.card.requestIntro')}
        </Button>
        {c.website && (
          <a href={c.website} target="_blank" rel="noopener noreferrer nofollow" className="p-2 rounded-md border border-[#DDE1E5] text-[#66707A] hover:text-[#527F19] hover:border-[#527F19]/40 transition-colors" aria-label={t('marketplace.card.website')} data-testid={`consultant-website-${c.consultant_id}`}>
            <ExternalLink className="w-4 h-4" />
          </a>
        )}
        {c.linkedin && (
          <a href={c.linkedin} target="_blank" rel="noopener noreferrer nofollow" className="p-2 rounded-md border border-[#DDE1E5] text-[#66707A] hover:text-[#527F19] hover:border-[#527F19]/40 transition-colors" aria-label={t('marketplace.card.linkedin')} data-testid={`consultant-linkedin-${c.consultant_id}`}>
            <Linkedin className="w-4 h-4" />
          </a>
        )}
      </div>
    </div>
  );
}

export default function ConsultantsPage() {
  const { t } = useTranslation();
  const { lang } = useLocale();
  usePageTitle(t('marketplace.meta.title'));
  useMetaDescription(t('marketplace.meta.description'));
  useSocialMeta({
    title: t('marketplace.meta.title'),
    description: t('marketplace.meta.description'),
    url: `https://ismscalculator.com/${lang}/consultants`,
  });

  const [consultants, setConsultants] = useState([]);
  const [facets, setFacets] = useState({ languages: [], regions: [], specialties: [] });
  const [hasSamples, setHasSamples] = useState(false);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ language: ALL, region: ALL, specialty: ALL, q: '' });

  const [active, setActive] = useState(null); // consultant for intro dialog
  const [form, setForm] = useState({ name: '', email: '', company: '', message: '', consent: false });
  const [sending, setSending] = useState(false);

  const [applyOpen, setApplyOpen] = useState(false);
  const [applyForm, setApplyForm] = useState(APPLY_EMPTY);
  const [applying, setApplying] = useState(false);

  const fetchConsultants = useCallback(async (f) => {
    setLoading(true);
    try {
      const params = {};
      if (f.language !== ALL) params.language = f.language;
      if (f.region !== ALL) params.region = f.region;
      if (f.specialty !== ALL) params.specialty = f.specialty;
      if (f.q.trim()) params.q = f.q.trim();
      const res = await axios.get(`${API}/consultants`, { params });
      setConsultants(res.data.consultants);
      setFacets(res.data.facets);
      setHasSamples(res.data.has_samples);
    } catch (err) {
      logger.warn('[Consultants] fetch failed:', err?.message || err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const id = setTimeout(() => fetchConsultants(filters), 250);
    return () => clearTimeout(id);
  }, [filters, fetchConsultants]);

  const setFilter = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));
  const clearFilters = () => setFilters({ language: ALL, region: ALL, specialty: ALL, q: '' });
  const hasActiveFilters = filters.language !== ALL || filters.region !== ALL || filters.specialty !== ALL || filters.q.trim();

  const openRequest = (c) => {
    setActive(c);
    setForm({ name: '', email: '', company: '', message: '', consent: false });
  };

  const submitIntro = async (e) => {
    e.preventDefault();
    if (!EMAIL_RE.test(form.email.trim())) { toast.error(t('marketplace.errors.invalidEmail')); return; }
    if (!form.consent) { toast.error(t('marketplace.errors.consent')); return; }
    setSending(true);
    try {
      await axios.post(`${API}/marketplace/request`, {
        consultant_id: active.consultant_id,
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim() || null,
        message: form.message.trim(),
        lang,
        consent: form.consent,
      });
      toast.success(t('marketplace.intro.success'));
      setActive(null);
    } catch (err) {
      logger.warn('[Consultants] intro failed:', err?.message || err);
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : t('marketplace.errors.failed'));
    } finally {
      setSending(false);
    }
  };

  const submitApply = async (e) => {
    e.preventDefault();
    if (applyForm.name.trim().length < 2) { toast.error(t('marketplace.errors.nameTooShort')); return; }
    if (!EMAIL_RE.test(applyForm.email.trim())) { toast.error(t('marketplace.errors.invalidEmail')); return; }
    if (!applyForm.consent) { toast.error(t('marketplace.errors.consent')); return; }
    setApplying(true);
    try {
      await axios.post(`${API}/consultants/apply`, {
        name: applyForm.name.trim(),
        email: applyForm.email.trim(),
        title: applyForm.jobTitle.trim(),
        company: applyForm.company.trim(),
        bio: applyForm.bio.trim(),
        specialties: toArr(applyForm.specialties),
        regions: toArr(applyForm.regions),
        languages: toArr(applyForm.languages),
        rate_range: applyForm.rate_range.trim(),
        years_experience: parseInt(applyForm.years_experience, 10) || 0,
        website: applyForm.website.trim() || null,
        linkedin: applyForm.linkedin.trim() || null,
        consent: applyForm.consent,
      });
      toast.success(t('marketplace.apply.success'));
      setApplyOpen(false);
      setApplyForm(APPLY_EMPTY);
    } catch (err) {
      logger.warn('[Consultants] apply failed:', err?.message || err);
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : t('marketplace.errors.failed'));
    } finally {
      setApplying(false);
    }
  };

  const FilterSelect = ({ id, value, options, placeholder }) => (
    <Select value={value} onValueChange={(v) => setFilter(id, v)}>
      <SelectTrigger className="bg-white border-[#DDE1E5]" aria-label={placeholder} data-testid={`filter-${id}`}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className="bg-white max-h-72">
        <SelectItem value={ALL}>{t('marketplace.filters.all')} · {placeholder}</SelectItem>
        {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
      </SelectContent>
    </Select>
  );

  return (
    <PublicShell testId="consultants-page">
      <main id="main-content" tabIndex={-1} className="site-container public-main">
        <PageHeader title={t('marketplace.hero.title')} description={t('marketplace.hero.subtitle')} eyebrow={t('marketplace.hero.badge')} testId="consultants-heading" />

        {hasSamples && (
          <div className="mb-6 flex items-start gap-2.5 rounded-lg border border-[#B7791F]/25 bg-[#B7791F]/[0.06] px-4 py-3 text-sm text-[#8A5A0B]" data-testid="sample-banner">
            <Sparkles className="w-4 h-4 mt-0.5 shrink-0" /> {t('marketplace.sampleBanner')}
          </div>
        )}

        {/* Filters */}
        <div className="border-y border-[#DDE1E5] py-5 mb-6 flex flex-col gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A929B]" />
            <Input
              value={filters.q}
              onChange={(e) => setFilter('q', e.target.value)}
              placeholder={t('marketplace.filters.searchPlaceholder')}
              aria-label={t('marketplace.filters.searchPlaceholder')}
              className="pl-9 bg-white border-[#DDE1E5]"
              data-testid="consultant-search-input"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 lg:flex">
            <div className="min-w-[150px]"><FilterSelect id="language" value={filters.language} options={facets.languages} placeholder={t('marketplace.filters.language')} /></div>
            <div className="min-w-[150px]"><FilterSelect id="region" value={filters.region} options={facets.regions} placeholder={t('marketplace.filters.region')} /></div>
            <div className="min-w-[150px]"><FilterSelect id="specialty" value={filters.specialty} options={facets.specialties} placeholder={t('marketplace.filters.specialty')} /></div>
          </div>
          {hasActiveFilters && (
            <Button variant="ghost" onClick={clearFilters} className="text-[#66707A] hover:text-[#527F19] shrink-0" data-testid="clear-filters-btn">
              <X className="w-4 h-4 mr-1" /> {t('marketplace.filters.clear')}
            </Button>
          )}
        </div>

        {/* Results */}
        {(() => {
          if (loading) {
            return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#527F19]" /></div>;
          }
          if (consultants.length === 0) {
            return (
              <div className="text-center py-20" data-testid="consultants-empty">
                <div className="w-14 h-14 rounded-lg bg-[#527F19]/10 flex items-center justify-center mx-auto mb-4">
                  <Users className="w-7 h-7 text-[#527F19]" />
                </div>
                <h3 className="font-display text-lg font-semibold text-[#171A1D]">{t('marketplace.empty.title')}</h3>
                <p className="text-sm text-[#66707A] mt-1">{t('marketplace.empty.desc')}</p>
              </div>
            );
          }
          return (
            <>
              <p className="text-sm text-[#8A929B] mb-4" data-testid="consultants-count">{t('marketplace.filters.results', { count: consultants.length })}</p>
              <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5 stagger-children">
                {consultants.map((c) => (
                  <ConsultantCard key={c.consultant_id} c={c} onRequest={openRequest} t={t} />
                ))}
              </div>
            </>
          );
        })()}

        {/* Apply-to-be-listed CTA — dark brand band */}
        <div className="mt-14 border-y border-[#DDE1E5] py-8" data-testid="apply-cta">
          <div className="absolute inset-0 bg-grid pointer-events-none" aria-hidden="true" />
          <div className="relative flex flex-col sm:flex-row items-start sm:items-center gap-6 justify-between">
            <div>
              <h2>{t('marketplace.apply.ctaTitle')}</h2>
              <p className="text-sm text-[#8A929B] mt-2 max-w-xl">{t('marketplace.apply.ctaDesc')}</p>
            </div>
            <Button variant="outline" onClick={() => { setApplyForm(APPLY_EMPTY); setApplyOpen(true); }} data-testid="apply-open-btn">
              <Briefcase className="w-4 h-4 mr-2" /> {t('marketplace.apply.ctaButton')}
            </Button>
          </div>
        </div>
      </main>

      {/* Intro dialog */}
      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="bg-white max-w-md" data-testid="intro-dialog">
          <DialogHeader>
            <DialogTitle className="font-display font-semibold tracking-tight">{t('marketplace.intro.title')}</DialogTitle>
            <DialogDescription>{t('marketplace.intro.subtitle', { name: active?.name || '' })}</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitIntro} className="space-y-3" noValidate>
            <div>
              <Label htmlFor="intro-name" className="text-sm">{t('marketplace.intro.name')}</Label>
              <Input id="intro-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t('marketplace.intro.namePh')} className="mt-1" data-testid="intro-name-input" />
            </div>
            <div>
              <Label htmlFor="intro-email" className="text-sm">{t('marketplace.intro.email')} *</Label>
              <Input id="intro-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={t('marketplace.intro.emailPh')} className="mt-1" data-testid="intro-email-input" />
            </div>
            <div>
              <Label htmlFor="intro-company" className="text-sm">{t('marketplace.intro.company')}</Label>
              <Input id="intro-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder={t('marketplace.intro.companyPh')} className="mt-1" data-testid="intro-company-input" />
            </div>
            <div>
              <Label htmlFor="intro-message" className="text-sm">{t('marketplace.intro.message')}</Label>
              <Textarea id="intro-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder={t('marketplace.intro.messagePh')} className="mt-1 min-h-[80px]" data-testid="intro-message-input" />
            </div>
            <div className="flex items-start gap-2.5">
              <Checkbox id="intro-consent" checked={form.consent} onCheckedChange={(v) => setForm({ ...form, consent: v === true })} className="mt-0.5" data-testid="intro-consent-checkbox" />
              <Label htmlFor="intro-consent" className="text-xs text-[#66707A] leading-relaxed font-normal cursor-pointer">
                {t('marketplace.intro.consent')}{' '}
                <LocaleLink to="/privacy" className="text-[#527F19] hover:underline">{t('marketplace.intro.privacyLink')}</LocaleLink>.
              </Label>
            </div>
            <Button type="submit" disabled={sending} className="w-full bg-[#527F19] hover:bg-[#3F6114] text-white font-semibold shadow-card transition-colors" data-testid="intro-submit-btn">
              {sending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t('marketplace.intro.sending')}</> : <><Send className="w-4 h-4 mr-2" />{t('marketplace.intro.send')}</>}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Apply-to-be-listed dialog */}
      <Dialog open={applyOpen} onOpenChange={setApplyOpen}>
        <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid="apply-dialog">
          <DialogHeader>
            <DialogTitle className="font-display font-semibold tracking-tight">{t('marketplace.apply.title')}</DialogTitle>
            <DialogDescription>{t('marketplace.apply.subtitle')}</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitApply} className="space-y-3" noValidate>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label className="text-sm">{t('marketplace.apply.name')} *</Label><Input aria-label={t('marketplace.apply.name')} value={applyForm.name} onChange={(e) => setApplyForm({ ...applyForm, name: e.target.value })} className="mt-1" data-testid="apply-name-input" /></div>
              <div><Label className="text-sm">{t('marketplace.apply.email')} *</Label><Input aria-label={t('marketplace.apply.email')} type="email" value={applyForm.email} onChange={(e) => setApplyForm({ ...applyForm, email: e.target.value })} className="mt-1" data-testid="apply-email-input" /></div>
              <div><Label className="text-sm">{t('marketplace.apply.jobTitle')}</Label><Input aria-label={t('marketplace.apply.jobTitle')} value={applyForm.jobTitle} onChange={(e) => setApplyForm({ ...applyForm, jobTitle: e.target.value })} className="mt-1" data-testid="apply-title-input" /></div>
              <div><Label className="text-sm">{t('marketplace.apply.company')}</Label><Input aria-label={t('marketplace.apply.company')} data-testid="apply-company-input" value={applyForm.company} onChange={(e) => setApplyForm({ ...applyForm, company: e.target.value })} className="mt-1" /></div>
            </div>
            <div><Label className="text-sm">{t('marketplace.apply.bio')}</Label><Textarea aria-label={t('marketplace.apply.bio')} data-testid="apply-bio-input" value={applyForm.bio} onChange={(e) => setApplyForm({ ...applyForm, bio: e.target.value })} className="mt-1 min-h-[70px]" /></div>
            <div><Label className="text-sm">{t('marketplace.apply.specialties')}</Label><Input aria-label={t('marketplace.apply.specialties')} data-testid="apply-specialties-input" value={applyForm.specialties} onChange={(e) => setApplyForm({ ...applyForm, specialties: e.target.value })} className="mt-1" placeholder={t('marketplace.apply.listHint')} /></div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label className="text-sm">{t('marketplace.apply.regions')}</Label><Input aria-label={t('marketplace.apply.regions')} data-testid="apply-regions-input" value={applyForm.regions} onChange={(e) => setApplyForm({ ...applyForm, regions: e.target.value })} className="mt-1" placeholder={t('marketplace.apply.listHint')} /></div>
              <div><Label className="text-sm">{t('marketplace.apply.languages')}</Label><Input aria-label={t('marketplace.apply.languages')} data-testid="apply-languages-input" value={applyForm.languages} onChange={(e) => setApplyForm({ ...applyForm, languages: e.target.value })} className="mt-1" placeholder={t('marketplace.apply.listHint')} /></div>
              <div><Label className="text-sm">{t('marketplace.apply.rate')}</Label><Input aria-label={t('marketplace.apply.rate')} data-testid="apply-rate-input" value={applyForm.rate_range} onChange={(e) => setApplyForm({ ...applyForm, rate_range: e.target.value })} className="mt-1" /></div>
              <div><Label className="text-sm">{t('marketplace.apply.experience')}</Label><Input aria-label={t('marketplace.apply.experience')} data-testid="apply-experience-input" type="number" min="0" value={applyForm.years_experience} onChange={(e) => setApplyForm({ ...applyForm, years_experience: e.target.value })} className="mt-1" /></div>
              <div><Label className="text-sm">{t('marketplace.apply.website')}</Label><Input aria-label={t('marketplace.apply.website')} data-testid="apply-website-input" value={applyForm.website} onChange={(e) => setApplyForm({ ...applyForm, website: e.target.value })} className="mt-1" /></div>
              <div><Label className="text-sm">{t('marketplace.apply.linkedin')}</Label><Input aria-label={t('marketplace.apply.linkedin')} data-testid="apply-linkedin-input" value={applyForm.linkedin} onChange={(e) => setApplyForm({ ...applyForm, linkedin: e.target.value })} className="mt-1" /></div>
            </div>
            <div className="flex items-start gap-2.5">
              <Checkbox id="apply-consent" checked={applyForm.consent} onCheckedChange={(v) => setApplyForm({ ...applyForm, consent: v === true })} className="mt-0.5" data-testid="apply-consent-checkbox" />
              <Label htmlFor="apply-consent" className="text-xs text-[#66707A] leading-relaxed font-normal cursor-pointer">
                {t('marketplace.apply.consent')}{' '}
                <LocaleLink to="/privacy" className="text-[#527F19] hover:underline">{t('marketplace.intro.privacyLink')}</LocaleLink>.
              </Label>
            </div>
            <Button type="submit" disabled={applying} className="w-full bg-[#527F19] hover:bg-[#3F6114] text-white font-semibold shadow-card transition-colors" data-testid="apply-submit-btn">
              {applying ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t('marketplace.apply.submitting')}</> : <><Send className="w-4 h-4 mr-2" />{t('marketplace.apply.submit')}</>}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </PublicShell>
  );
}
